import { useAppDispatch, useAppSelector } from '../../redux/store';
import DocumentCard from './document-card';
import { Document } from '../../models/document';
import { PlusIcon, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { documentSelectByQuery, DBDocumentThunks } from '../../redux/thunks/DBServiceThunks';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { createSelector } from '@reduxjs/toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import { selectCanEditCurrentCase } from '../../redux/selectors/knowledgebaseSelectors';

// Extending the document type to include 'excel'
type ExtendedDocumentType = Document['type'] | 'excel';

interface DocumentsGridProps {
    knowledgebaseId: string;
    onUploadClick: () => void;
    searchTerm?: string;
}

// Memoized selector for documents
const selectDocuments = createSelector(
    [(state) => state.DBDocumentReducer, (_, props) => props.knowledgebaseId],
    (documentReducer, knowledgebaseId) =>
        documentSelectByQuery(documentReducer, { knowledgebaseId })
);

export default function DocumentsGrid({ knowledgebaseId, onUploadClick, searchTerm = '' }: DocumentsGridProps) {
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();

    const viewMode = useAppSelector(state => state.UIReducer.viewMode);
    const documents = useAppSelector(state => selectDocuments(state, { knowledgebaseId }));
    const canEdit = useAppSelector(selectCanEditCurrentCase);

    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean, documentId: string, documentName: string }>({
        open: false,
        documentId: '',
        documentName: ''
    });

    const handleDeleteClick = (event: React.MouseEvent, document: Document) => {
        event.stopPropagation();
        setDeleteDialog({
            open: true,
            documentId: document._id,
            documentName: document.name
        });
    };

    const handleDeleteConfirm = () => {
        dispatch(DBDocumentThunks.delete({ entity: { _id: deleteDialog.documentId } }));
        setDeleteDialog({ open: false, documentId: '', documentName: '' });
    };

    const filteredDocuments = useMemo(() => {
        console.log('[GoogleDriveUpload] DocumentsGrid: All documents:', documents);
        if (!documents) return [];

        const filtered = documents.filter(document => {
            if (!searchTerm) return true;

            const searchLower = searchTerm.toLowerCase();
            return (
                document.name.toLowerCase().includes(searchLower) ||
                (document.description?.toLowerCase().includes(searchLower) || false)
            );
        });
        console.log('[GoogleDriveUpload] DocumentsGrid: Filtered documents:', filtered);
        return filtered;
    }, [documents, searchTerm]);

    return (
        <div
            dir={getLanguageDirection(i18n.language)}
            className="p-6 w-full h-full overflow-auto"
        >
            {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Upload new document card */}
                    {canEdit && (
                        <div
                            onClick={onUploadClick}
                            className="min-h-[180px] flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-dashed border-gold/40 bg-gold/[0.04] cursor-pointer transition-all duration-200 hover:bg-gold/[0.08] hover:border-gold hover:-translate-y-0.5 hover:shadow-md"
                        >
                            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                                <PlusIcon size={24} className="text-gold" />
                            </div>
                            <p className="font-medium text-gold text-sm">
                                {t('common.knowledgebase.uploadDocument')}
                            </p>
                            <p className="text-muted-foreground text-xs text-center">
                                {t('common.knowledgebase.addNewFiles')}
                            </p>
                        </div>
                    )}

                    {/* Document cards */}
                    {filteredDocuments.map(document => (
                        <DocumentCard key={document._id} document={document} />
                    ))}
                </div>
            ) : (
                <div className="w-full rounded-2xl border border-border overflow-hidden bg-card">
                    {/* Upload row in list view */}
                    {canEdit && (
                        <div className="bg-gold/[0.04] border-b border-border">
                            <button
                                onClick={onUploadClick}
                                className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gold/[0.08] transition-colors"
                            >
                                <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                                    <PlusIcon size={20} className="text-gold" />
                                </div>
                                <div>
                                    <p className="font-medium text-gold text-sm">
                                        {t('common.knowledgebase.uploadNewDocument')}
                                    </p>
                                    <p className="text-muted-foreground text-xs">
                                        {t('common.knowledgebase.addFilesToKnowledgeBase')}
                                    </p>
                                </div>
                            </button>
                        </div>
                    )}

                    {/* Document list items */}
                    {filteredDocuments.map((document, index) => (
                        <div
                            key={document._id}
                            className={`group flex items-center gap-3 px-6 py-4 hover:bg-background/60 transition-colors ${
                                index < filteredDocuments.length - 1 ? 'border-b border-border' : ''
                            }`}
                        >
                            {/* Icon */}
                            <div className="w-10 h-10 flex items-center justify-center rounded-lg shrink-0">
                                {getDocumentIcon(document.type)}
                            </div>

                            {/* Text */}
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground text-sm truncate">
                                    {document.name}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {document.metadata?.contentLength && formatFileSize(document.metadata.contentLength)}
                                    {document.createdAt && document.metadata?.contentLength && ' · '}
                                    {document.createdAt && new Date(document.createdAt).toLocaleDateString()}
                                </p>
                                {document.description && (
                                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                        {document.description}
                                    </p>
                                )}
                            </div>

                            {/* Delete button — revealed on row hover */}
                            {canEdit && (
                                <button
                                    title={t('common.tooltips.delete')}
                                    aria-label={t('common.buttons.delete')}
                                    onClick={(e) => handleDeleteClick(e, document)}
                                    className="p-2 rounded-lg text-destructive opacity-0 group-hover:opacity-100 hover:bg-destructive/10 transition-all shrink-0"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <ConfirmationDialog
                open={deleteDialog.open}
                title={t('common.knowledgebase.deleteDocumentTitle')}
                message={`${t('common.knowledgebase.deleteDocumentMessage')}${deleteDialog.documentName}?`}
                itemName={t('common.knowledgebase.deleteDocumentItemName')}
                confirmButtonText={t('common.knowledgebase.deleteButtonText')}
                isDestructive={true}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, documentId: '', documentName: '' })}
            />
        </div>
    );
}

function getDocumentIcon(type: Document['type']) {
    const documentType = type as ExtendedDocumentType;

    switch (documentType) {
        case 'pdf':
            return (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 4H12C9.79086 4 8 5.79086 8 8V40C8 42.2091 9.79086 44 12 44H36C38.2091 44 40 42.2091 40 40V16L28 4Z" fill="#FFF5F5" stroke="#E83A3A" strokeWidth="1.5" />
                    <path d="M28 4V16H40" stroke="#E83A3A" strokeWidth="1.5" />
                    <rect x="13" y="22" width="22" height="16" rx="1" fill="#E83A3A" />
                    <path d="M15.9 33V27H17.46C17.9 27 18.264 27.084 18.552 27.252C18.844 27.416 19.06 27.648 19.2 27.948C19.344 28.244 19.416 28.584 19.416 28.968C19.416 29.352 19.344 29.692 19.2 29.988C19.06 30.284 18.844 30.516 18.552 30.684C18.264 30.848 17.9 30.93 17.46 30.93H16.812V33H15.9ZM16.812 30.198H17.412C17.652 30.198 17.852 30.156 18.012 30.072C18.172 29.984 18.292 29.858 18.372 29.694C18.452 29.53 18.492 29.332 18.492 29.1C18.492 28.868 18.452 28.67 18.372 28.506C18.292 28.342 18.172 28.218 18.012 28.134C17.852 28.046 17.652 28.002 17.412 28.002H16.812V30.198Z" fill="white" />
                    <path d="M20 33V27H22C22.5 27 22.9 27.1 23.1 27.3C23.4 27.5 23.5 27.7 23.5 28C23.5 28.2 23.4 28.4 23.3 28.6C23.2 28.8 23 28.9 22.7 29V29C23 29 23.3 29.1 23.5 29.3C23.7 29.5 23.8 29.8 23.8 30.1C23.8 30.5 23.7 30.8 23.4 31C23.1 31.2 22.7 31.3 22.2 31.3H20V33Z" fill="white" />
                    <path d="M25 33V27H28C28.3 27 28.6 27.1 28.9 27.2C29.1 27.3 29.3 27.5 29.4 27.7C29.5 27.9 29.6 28.2 29.6 28.4C29.6 28.6 29.5 28.8 29.5 29C29.4 29.1 29.3 29.3 29.2 29.4C29.1 29.5 28.9 29.6 28.8 29.6V29.7C29 29.7 29.2 29.8 29.3 29.9C29.5 30 29.6 30.2 29.7 30.4C29.8 30.6 29.9 30.8 29.9 31.1C29.9 31.4 29.8 31.6 29.7 31.9C29.6 32.1 29.4 32.3 29.1 32.4C28.9 32.5 28.6 32.6 28.2 32.6C28.1 32.6 27.9 32.6 27.8 32.5C27.7 32.5 27.6 32.5 27.5 32.5C27.4 32.5 27.3 32.5 27.3 32.5V31.6C27.4 31.6 27.5 31.7 27.6 31.7C27.7 31.7 27.8 31.7 27.9 31.7C28.1 31.7 28.3 31.6 28.4 31.6C28.5 31.5 28.6 31.4 28.6 31.3C28.7 31.2 28.7 31.1 28.7 31C28.7 30.8 28.6 30.6 28.6 30.5C28.5 30.4 28.4 30.3 28.3 30.3C28.2 30.2 28 30.2 27.8 30.2H27.3V29.3H27.8C28 29.3 28.1 29.3 28.3 29.2C28.4 29.2 28.5 29.1 28.5 29C28.6 28.9 28.6 28.8 28.6 28.7C28.6 28.5 28.6 28.3 28.5 28.2C28.4 28.1 28.2 28.1 28 28.1C28 28.1 27.9 28.1 27.8 28.1C27.7 28.1 27.7 28.1 27.6 28.2C27.5 28.2 27.5 28.2 27.4 28.2V27.3C27.5 27.3 27.5 27.3 27.6 27.2C27.7 27.2 27.8 27.2 27.9 27.2C28 27.2 28.1 27.2 28.2 27.2C28.5 27.2 28.7 27.2 28.9 27.3C29.1 27.4 29.3 27.5 29.4 27.7C29.5 27.9 29.5 28.1 29.5 28.3C29.5 28.5 29.5 28.7 29.4 28.9C29.3 29.1 29.2 29.2 29.1 29.2C28.9 29.3 28.8 29.4 28.6 29.4V29.5C28.8 29.5 29 29.5 29.2 29.6C29.3 29.7 29.5 29.9 29.6 30.1C29.7 30.2 29.7 30.5 29.7 30.7C29.7 31 29.6 31.2 29.5 31.4C29.4 31.6 29.2 31.8 29 31.9C28.8 32 28.5 32.1 28.2 32.2C28.1 32.2 28 32.2 28 32.2C27.9 32.2 27.8 32.2 27.7 32.2C27.7 32.2 27.6 32.2 27.5 32.2C27.4 32.2 27.3 32.1 27.2 32.1C27.1 32.1 27 32 26.9 32L27 31.3C27.1 31.3 27.1 31.3 27.2 31.4C27.2 31.4 27.3 31.4 27.3 31.4C27.4 31.4 27.4 31.4 27.5 31.4H27.8C27.8 31.4 27.8 31.4 27.8 31.4C27.8 31.4 27.8 31.4 27.8 31.3V29.3H27.3V33H25Z" fill="white" />
                </svg>
            );
        case 'word':
            return (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 4H12C9.79086 4 8 5.79086 8 8V40C8 42.2091 9.79086 44 12 44H36C38.2091 44 40 42.2091 40 40V16L28 4Z" fill="#EFF6FC" stroke="#2B579A" strokeWidth="1.5" />
                    <path d="M28 4V16H40" stroke="#2B579A" strokeWidth="1.5" />
                    <rect x="14" y="20" width="20" height="18" rx="1" fill="#2B579A" />
                    <path d="M18.7601 33L17.2201 26.2H18.9001L19.7201 30.36L20.6201 26.2H22.0801L22.9401 30.38L23.7801 26.2H25.3801L23.8601 33H22.2601L21.3401 28.84L20.4401 33H18.7601Z" fill="white" />
                </svg>
            );
        case 'text':
            return (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 4H12C9.79086 4 8 5.79086 8 8V40C8 42.2091 9.79086 44 12 44H36C38.2091 44 40 42.2091 40 40V16L28 4Z" fill="#F8F9FA" stroke="#6E7681" strokeWidth="1.5" />
                    <path d="M28 4V16H40" stroke="#6E7681" strokeWidth="1.5" />
                    <path d="M16 22H32M16 26H32M16 30H28" stroke="#6E7681" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            );
        case 'html':
            return (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 4H12C9.79086 4 8 5.79086 8 8V40C8 42.2091 9.79086 44 12 44H36C38.2091 44 40 42.2091 40 40V16L28 4Z" fill="#FEF8EE" stroke="#E34C26" strokeWidth="1.5" />
                    <path d="M28 4V16H40" stroke="#E34C26" strokeWidth="1.5" />
                    <rect x="13.5" y="20" width="21" height="18" rx="1" fill="#E34C26" />
                    <path d="M19 25L16 28L19 31" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M29 25L32 28L29 31" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22 33L26 23" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case 'excel':
            return (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 4H12C9.79086 4 8 5.79086 8 8V40C8 42.2091 9.79086 44 12 44H36C38.2091 44 40 42.2091 40 40V16L28 4Z" fill="#E9F7EE" stroke="#217346" strokeWidth="1.5" />
                    <path d="M28 4V16H40" stroke="#217346" strokeWidth="1.5" />
                    <rect x="14" y="20" width="20" height="18" rx="1" fill="#217346" />
                    <path d="M18.7659 33L20.4259 29.9L18.8859 26.2H20.6859L21.5059 28.32L22.4259 26.2H24.1859L22.6659 29.92L24.3259 33H22.5059L21.5259 30.76L20.5859 33H18.7659Z" fill="white" />
                    <path d="M25.4241 33V31.44H29.0241V33H25.4241ZM25.4241 29.8V28.32H29.0241V29.8H25.4241ZM25.4241 26.2V24.72H29.0241V26.2H25.4241Z" fill="white" />
                </svg>
            );
        default:
            return (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M28 4H12C9.79086 4 8 5.79086 8 8V40C8 42.2091 9.79086 44 12 44H36C38.2091 44 40 42.2091 40 40V16L28 4Z" fill="#F9FAFB" stroke="#6E7681" strokeWidth="1.5" />
                    <path d="M28 4V16H40" stroke="#6E7681" strokeWidth="1.5" />
                    <path d="M16 22H32M16 26H32M16 30H28M16 34H24" stroke="#6E7681" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            );
    }
}

function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
